import * as z from "zod";
import { Controller, useWatch, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader, Save } from "lucide-react";
import {
  Field,
  FieldContent,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BottomFixed from "@/components/bottom-fixed";
import AsyncSelect from "@/components/async-select";
import type { alertFormSchema } from "./schemas";
import type { ApiNotificationRule } from "../../types";
import type { ApiDilemma } from "@/features/dilemmas/types";
import { SEARCH_ENDPOINTS } from "@/constants/combobox";

interface AlertFormProps {
  form: UseFormReturn<z.infer<typeof alertFormSchema>>;
  onSubmit: (data: z.infer<typeof alertFormSchema>) => void;
  isLoading: boolean;
  alert?: ApiNotificationRule;
}

const AlertForm = ({ form, onSubmit, isLoading, alert }: AlertFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alertType = useWatch({ control: form.control, name: "alert_type" });

  return (
    <div className="p-4 rounded-md border w-full flex flex-col gap-y-4 bg-white">
      <form
        id="alert-form"
        className="flex flex-col gap-y-5"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FieldGroup>
          {/* Title */}
          <Controller
            name="title"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="title-input">
                  {t("modules.alerts.keys.title")}{" "}
                  <span className="text-destructive">*</span>
                </FieldLabel>
                <Input
                  {...field}
                  id="title-input"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* Message */}
          <Controller
            name="message"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="message-textarea">
                  {t("modules.alerts.keys.message")}{" "}
                  <span className="text-destructive">*</span>
                </FieldLabel>
                <Textarea
                  {...field}
                  id="message-textarea"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          <div className="flex-1 flex flex-col gap-6">
            {/* Alert Type */}
            <Controller
              name="alert_type"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="alert-type-select">
                    {t("modules.alerts.keys.alert_type_label")}
                  </FieldLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="alert-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new_dilemma">
                        {t("modules.alerts.keys.alert_type_new_dilemma")}
                      </SelectItem>
                      <SelectItem value="focus_dilemma">
                        {t("modules.alerts.keys.alert_type_focus_dilemma")}
                      </SelectItem>
                      <SelectItem value="admin_action">
                        {t("modules.alerts.keys.alert_type_admin_action")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />

            {/* Dilemma picker — only when focus_dilemma */}
            {alertType === "focus_dilemma" && (
              <Controller
                name="dilemma"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel>
                      {t("modules.alerts.keys.dilemma_label")}
                    </FieldLabel>
                    <AsyncSelect<ApiDilemma>
                      url={SEARCH_ENDPOINTS.DILEMMAS}
                      getOptionLabel={(d) => d.name}
                      getOptionValue={(d) => d.id}
                      value={field.value ?? null}
                      onChange={field.onChange}
                      placeholder={t("modules.alerts.keys.dilemma_placeholder")}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />
            )}
          </div>

          {/* Frequency */}
          <Controller
            name="frequency"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field className="flex-1" data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="frequency-select">
                  {t("modules.alerts.keys.frequency_label")}
                </FieldLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">
                      {t("modules.alerts.keys.frequency_immediate")}
                    </SelectItem>
                    <SelectItem value="weekly">
                      {t("modules.alerts.keys.frequency_weekly")}
                    </SelectItem>
                    <SelectItem value="monthly">
                      {t("modules.alerts.keys.frequency_monthly")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* Notification Channels */}
          <div className="flex flex-col gap-y-3">
            <span className="text-sm font-medium">
              {t("modules.alerts.keys.channels_label")}
            </span>
            <div className="flex flex-col gap-y-3 max-w-44">
              <Field orientation="horizontal">
                <FieldContent>
                  <FieldLabel>
                    {t("modules.alerts.keys.channel_app")}
                  </FieldLabel>
                </FieldContent>
                <Switch checked disabled />
              </Field>
              <Controller
                name="send_email"
                control={form.control}
                render={({ field }) => (
                  <Field orientation="horizontal">
                    <FieldContent>
                      <FieldLabel htmlFor="send-email-switch">
                        {t("modules.alerts.keys.channel_email")}
                      </FieldLabel>
                    </FieldContent>
                    <Switch
                      id="send-email-switch"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </Field>
                )}
              />
            </div>
          </div>

          {/* Status */}
          <Controller
            name="is_active"
            control={form.control}
            render={({ field }) => (
              <Field orientation="horizontal" className="max-w-44 mt-4">
                <FieldContent>
                  <FieldLabel htmlFor="is-active-switch">
                    {t("modules.alerts.keys.status_label")}
                  </FieldLabel>
                </FieldContent>
                <Switch
                  id="is-active-switch"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </Field>
            )}
          />
        </FieldGroup>
      </form>

      <BottomFixed className="bg-white border-t border-l">
        <div className="flex flex-wrap flex-row items-center sm:justify-end gap-2">
          {alert && (
            <Button
              className="flex-1 sm:flex-none"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft />
              {t("keys.cancel")}
            </Button>
          )}
          <Button
            className="flex-1 sm:flex-none"
            disabled={isLoading}
            type="submit"
            form="alert-form"
          >
            {isLoading ? <Loader className="animate-spin" /> : <Save />}
            {t("keys.save")}
          </Button>
        </div>
      </BottomFixed>
    </div>
  );
};

export default AlertForm;
